import { supabase } from "@/integrations/supabase/client";

const FUNCTION_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/api-sports`;

export interface ApiSportsResponse<T> {
  get: string;
  parameters: Record<string, string>;
  errors: string[];
  results: number;
  response: T;
}

export interface League {
  id: number;
  name: string;
  type: string;
  logo: string;
  country: {
    name: string;
    code: string;
    flag: string;
  };
  seasons: {
    season: number;
    start: string;
    end: string;
  }[];
}

export interface Team {
  id: number;
  name: string;
  logo: string;
  national: boolean;
}

export interface Game {
  id: number;
  date: string;
  time: string;
  timestamp: number;
  timezone: string;
  week: string;
  status: {
    long: string;
    short: string;
  };
  league: {
    id: number;
    name: string;
    type: string;
    logo: string;
    season: number;
  };
  country: {
    id: number;
    name: string;
    code: string;
    flag: string;
  };
  teams: {
    home: Team;
    away: Team;
  };
  scores: {
    home: number | null;
    away: number | null;
  };
  periods: {
    first: { home: number | null; away: number | null };
    second: { home: number | null; away: number | null };
    third: { home: number | null; away: number | null };
    overtime: { home: number | null; away: number | null };
    penalties: { home: number | null; away: number | null };
  };
}

export interface Standing {
  position: number;
  team: Team;
  points: number;
  games: {
    played: number;
    win: { total: number; percentage: string };
    win_overtime?: { total: number; percentage: string };
    lose: { total: number; percentage: string };
    lose_overtime?: { total: number; percentage: string };
  };
  goals: {
    for: number;
    against: number;
  };
  form: string;
  description: string;
}

export interface GameEvent {
  type: string;
  time: string;
  period: string;
  minute: string;
  team: {
    id: number;
    name: string;
    logo?: string;
  };
  players: string[];
  assists: string[];
  comments?: string;
}
export interface Player {
  id: number;
  name: string;
  firstname: string;
  lastname: string;
  age: number;
  birth: {
    date: string;
    place: string;
    country: string;
  };
  nationality: string;
  height: string;
  weight: string;
  photo: string;
}

export interface PlayerStatistics {
  player: Player;
  statistics: {
    team: Team;
    league: {
      id: number;
      name: string;
      type: string;
      logo: string;
      season: number;
    };
    games: {
      appearences: number;
      minutes: number;
      position: string;
    };
    goals: {
      total: number;
      assists: number;
    };
    penalty: {
      minutes: number;
    };
    shots: {
      total: number;
      on: number;
    };
    powerplay: {
      goals: number;
      assists: number;
    };
    shorthanded: {
      goals: number;
      assists: number;
    };
    plusminus: number;
  }[];
}

export interface GoalieStatistics {
  player: Player;
  statistics: {
    team: Team;
    league: {
      id: number;
      name: string;
      type: string;
      logo: string;
      season: number;
    };
    games: {
      appearences: number;
      minutes: number;
      position: string;
    };
    goals: {
      against: number;
      saves: number;
    };
    wins: number;
    losses: number;
    ties: number;
    shutouts: number;
  }[];
}

async function fetchApiSports<T>(endpoint: string, params: Record<string, string> = {}, requireAuth = true): Promise<ApiSportsResponse<T>> {
  const searchParams = new URLSearchParams({ endpoint, ...params });
  
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  if (requireAuth) {
    const { data: sessionData } = await supabase.auth.getSession();
    
    if (!sessionData?.session?.access_token) {
      throw new Error('Authentication required to access sports data');
    }
    
    headers['Authorization'] = `Bearer ${sessionData.session.access_token}`;
  }

  const response = await fetch(`${FUNCTION_URL}?${searchParams}`, { headers });
  
  if (response.status === 401 && requireAuth) {
    throw new Error('Authentication required. Please sign in to access sports data.');
  }
  
  if (!response.ok) {
    throw new Error(`API-Sports request failed: ${response.statusText}`);
  }
  
  return response.json();
}

// League endpoints
export async function getLeagues(params?: { id?: number; name?: string; country?: string; season?: number }) {
  const stringParams: Record<string, string> = {};
  if (params?.id) stringParams.id = String(params.id);
  if (params?.name) stringParams.name = params.name;
  if (params?.country) stringParams.country = params.country;
  if (params?.season) stringParams.season = String(params.season);
  
  return fetchApiSports<League[]>('/leagues', stringParams);
}

// Games endpoints
export async function getGames(params: { id?: number; league?: number; season?: number; date?: string; team?: number; live?: string }) {
  const stringParams: Record<string, string> = {};
  if (params.id) stringParams.id = String(params.id);
  if (params.league) stringParams.league = String(params.league);
  if (params.season) stringParams.season = String(params.season);
  if (params.date) stringParams.date = params.date;
  if (params.team) stringParams.team = String(params.team);
  if (params.live) stringParams.live = params.live;
  
  return fetchApiSports<Game[]>('/games', stringParams);
}

// Fetch a single game by ID (public, no auth required)
export async function getGameById(gameId: number) {
  return fetchApiSports<Game[]>('/games', { id: String(gameId) }, false);
}

// Get live games
export async function getLiveGames() {
  return fetchApiSports<Game[]>('/games', { live: 'all' });
}

// Teams endpoints
export async function getTeams(params: { league?: number; season?: number; id?: number; name?: string }) {
  const stringParams: Record<string, string> = {};
  if (params.league) stringParams.league = String(params.league);
  if (params.season) stringParams.season = String(params.season);
  if (params.id) stringParams.id = String(params.id);
  if (params.name) stringParams.name = params.name;
  
  return fetchApiSports<{ team: Team }[]>('/teams', stringParams);
}

// Standings endpoints
export async function getStandings(params: { league: number; season: number; group?: string }) {
  const stringParams: Record<string, string> = {
    league: String(params.league),
    season: String(params.season),
  };
  if (params.group) stringParams.group = params.group;
  
  return fetchApiSports<Standing[][]>('/standings', stringParams);
}

// Public standings (no auth required) - used for league stats preview
export async function getPublicStandings(params: { league: number; season: number }) {
  const stringParams: Record<string, string> = {
    league: String(params.league),
    season: String(params.season),
  };
  
  return fetchApiSports<Standing[][]>('/standings', stringParams, false);
}

// Public games for a season (no auth required) - used for league stats when standings unavailable
export async function getPublicSeasonGames(params: { league: number; season: number }) {
  const stringParams: Record<string, string> = {
    league: String(params.league),
    season: String(params.season),
  };
  
  return fetchApiSports<Game[]>('/games', stringParams, false);
}

// Public games by date (no auth required) - used for scores section
export async function getPublicGames(params: { league?: number; season?: number; date?: string; team?: number }) {
  const stringParams: Record<string, string> = {};
  if (params.league) stringParams.league = String(params.league);
  if (params.season) stringParams.season = String(params.season);
  if (params.date) stringParams.date = params.date;
  if (params.team) stringParams.team = String(params.team);
  
  return fetchApiSports<Game[]>('/games', stringParams, false);
}

// Player statistics endpoints
export async function getPlayerStatistics(
  params: { league: number; season: number; team?: number; player?: number },
  requireAuth = true
) {
  const stringParams: Record<string, string> = {
    league: String(params.league),
    season: String(params.season),
  };
  if (params.team) stringParams.team = String(params.team);
  if (params.player) stringParams.player = String(params.player);
  
  return fetchApiSports<PlayerStatistics[]>('/players/statistics', stringParams, requireAuth);
}

// Player search
export async function searchPlayers(
  params: { search: string; league?: number; season?: number },
  requireAuth = true
) {
  const stringParams: Record<string, string> = {
    search: params.search,
  };
  if (params.league) stringParams.league = String(params.league);
  if (params.season) stringParams.season = String(params.season);
  
  return fetchApiSports<Player[]>('/players', stringParams, requireAuth);
}

// Top scorers
export async function getTopScorers(params: { league: number; season: number }) {
  const stringParams: Record<string, string> = {
    league: String(params.league),
    season: String(params.season),
  };
  
  return fetchApiSports<PlayerStatistics[]>('/players/top_scorers', stringParams);
}

// Top assists
export async function getTopAssists(params: { league: number; season: number }) {
  const stringParams: Record<string, string> = {
    league: String(params.league),
    season: String(params.season),
  };
  
  return fetchApiSports<PlayerStatistics[]>('/players/top_assists', stringParams);
}

// League IDs for common leagues - verified against API-Sports
export const LEAGUE_IDS = {
  NHL: 57,
  AHL: 58,
  ECHL: 59,
  KHL: 35,
  SHL: 47,
  ALLSVENSKAN: 48,
  LIIGA: 16,
  EXTRALIGA: 10,
  DEL: 19,
  NL: 51,
  // Women's Leagues
  PWHL: 225,
  SDHL: 203,
  NAISTEN_LIIGA: 126,
} as const;

export type LeagueKey = keyof typeof LEAGUE_IDS;
